import { Injectable } from "@angular/core";
import { SignoutRequestArgs, User, UserManager } from "oidc-client-ts";
import { environment } from "../../../environments/environment";
import { Store } from "@ngrx/store";
import { AppActions } from "../ngrx/app/app.reducer";
import { Router } from "@angular/router";
import { RootState } from "../ngrx/root.model";
import { AuthServiceDataToken, AuthServiceIdToken, AuthServiceLogoutToken } from "../constants/session.constants";

@Injectable({ providedIn: "root" })
export class AuthService {
  constructor(
    private store: Store<RootState>,
    private router: Router
  ) {
    this.base_uri = `${window.location.origin}`;
    this.oidcUserManager = new UserManager({
      authority: environment.keycloakAuthority,
      redirect_uri: `${this.base_uri}${environment.keycloakRedirectUri}`,
      post_logout_redirect_uri: `${this.base_uri}${environment.keycloakPostLogoutUri}`,
      client_id: environment.keycloakClientId,
      response_type: environment.keycloakResponseType,
      scope: environment.keycloakScope,
      automaticSilentRenew: true,
      accessTokenExpiringNotificationTimeInSeconds: 60,
    });
  }

  private base_uri: string;
  private oidcUserManager: UserManager;

  public getUserManager(): UserManager {
    return this.oidcUserManager;
  }

  public login(): Promise<void> {
    sessionStorage.clear(); // Ensure any existing session data is clear prior to login
    return this.oidcUserManager.signinRedirect();
  }

  public logout(): void {
    const signoutRequestArgs: SignoutRequestArgs = {
      url: `${this.base_uri}${environment.keycloakPostLogoutUri}`,
      post_logout_redirect_uri: `${this.base_uri}${environment.keycloakPostLogoutUri}`,
      client_id: environment.keycloakClientId,
      id_token_hint: `${sessionStorage.getItem(AuthServiceIdToken)}`,
    };

    this.store.dispatch(AppActions.SetAuthState({ userLoggedIn: false, userAuthData: null }));

    sessionStorage.clear(); // Clear all session data upon logout
    sessionStorage.setItem(AuthServiceLogoutToken, "true");

    this.router.navigate(["/login"]);
    this.oidcUserManager.signoutRedirect(signoutRequestArgs);
  }

  async completeLogin(bypassSignIn = false): Promise<User | null> {
    this.oidcUserManager.events.addUserLoaded(() => {
      this.oidcUserManager.getUser().then((user) => {
        if (user) {
          const authData = {
            accessToken: user.access_token,
            expiresAt: user.expires_at,
            profile: user.profile,
          };

          sessionStorage.setItem(AuthServiceDataToken, JSON.stringify(authData));
          sessionStorage.setItem(AuthServiceIdToken, `${user.id_token}`);
        }
      });
    });
    if (!bypassSignIn) {
      return this.oidcUserManager.signinRedirectCallback().then((user) => {
        return user;
      });
    } else {
      return null;
    }
  }
}
